import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Message {
  id: number;
  sender_id: number;
  message: string;
  sent_at: string;
  sender_type: 'user' | 'admin';
  is_read: boolean;
  username?: string;
  parent_message_id?: number;
}

interface User {
  id: number;
  username: string;
  email: string;
  suspension_reason?: string;
  suspended_at?: string;
}

interface AdminConversationModalProps {
  user: User;
  onClose: () => void;
  onMessageSent: () => void;
}

const AdminConversationModal: React.FC<AdminConversationModalProps> = ({ 
  user, 
  onClose, 
  onMessageSent 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadConversation();
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConversation = async () => {
    try {
      setLoading(true);
      // Get all messages from the inbox and filter for this user
      const response = await apiService.getAdminInbox();
      // Filter messages to show both messages FROM this user and TO this user
      const userMessages = response.messages.filter((msg: Message) => 
        msg.sender_id === user.id || msg.username === user.username
      );
      
      // Sort messages by sent_at ascending (oldest first)
      const sortedMessages = userMessages.sort((a, b) => 
        new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
      );
      
      setMessages(sortedMessages);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await apiService.sendAdminMessage(user.id, newMessage.trim());
      setNewMessage('');
      await loadConversation(); // Reload conversation
      onMessageSent(); // Notify parent to refresh inbox
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal-overlay conversation-modal-overlay">
      <div className="modal-container admin-conversation-modal">
        <div className="conversation-header">
          <div className="user-info">
            <h3>Conversation with {user.username}</h3>
            <div className="user-details">
              <span className="user-email">{user.email}</span>
              <span className="suspension-status">🚫 Suspended Account</span>
            </div>
            {user.suspension_reason && (
              <div className="suspension-reason">
                <strong>Suspension Reason:</strong> {user.suspension_reason}
              </div>
            )}
          </div>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="conversation-content">
          {loading ? (
            <div className="loading-state">
              <p>Loading conversation...</p>
            </div>
          ) : (
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages in this conversation yet.</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`message-bubble ${message.sender_type}`}
                  >
                    <div className="message-header">
                      <span className="sender">
                        {message.sender_type === 'admin' ? 'Admin' : user.username}
                      </span>
                      <span className="message-time">{formatDate(message.sent_at)}</span>
                    </div>
                    <div className="message-text">{message.message}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="message-input-container">
          <div className="input-group">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your response to the suspended user..."
              className="message-input"
              rows={3}
              disabled={sending}
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="send-message-btn"
            >
              {sending ? 'Sending...' : '📤 Send Reply'}
            </button>
          </div>
          <div className="input-note">
            <small>💡 The user will receive an email notification about your reply</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConversationModal;